import { createApi } from "../../../utils/createApi";
import { AxiosError, AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";
import { OnvifCredential } from "./onvifScanInterfaces";
import { useOnvifScanStore } from "../onvifScanStore/onvifScanStore";
import { pollOnvifScan } from "./pollOnvifScan";

interface StartOnvifScanArgs {
  tunnelId: number;
  credentials: OnvifCredential[];
  subnet?: string;
  navigate: NavigateFunction;
}

interface DiscoveryCreateResponse {
  task_id: string;
  status: string;
  tunnel_id: number;
  subnet: string;
  poll_url: string;
}

/**
 * Kick off a remote ONVIF network scan on the tunnel's WireGuard gateway
 * (POST /api/network-auto-discovery/) and begin polling for the result.
 * Mirrors the trigger+poll shape of CameraAutoAdd's handleRefreshCameraList.
 */
export const startOnvifScan = ({
  tunnelId,
  credentials,
  subnet,
  navigate,
}: StartOnvifScanArgs) => {
  const {
    setScanStatus,
    setTaskId,
    setHosts,
    setErrorMessage,
    setSelectedKeys,
    setScanCredentials,
  } = useOnvifScanStore.getState();

  // Only send credentials that have a username filled in.
  const cleanedCredentials = credentials.filter(
    (c) => c.username && c.username.trim() !== ""
  );

  setHosts([]);
  setSelectedKeys({});
  setErrorMessage("");
  setTaskId(null);
  // Freeze the exact list that goes to the scan: results identify the
  // authenticating credential by index into it, and the operator may edit the
  // credential rows (or the filter may drop some) before onboarding runs.
  setScanCredentials(cleanedCredentials);
  setScanStatus("pending");

  const AxiosUI = createApi("");

  const body: Record<string, unknown> = {
    tunnel_id: tunnelId,
    credentials: cleanedCredentials,
  };
  if (subnet && subnet.trim() !== "") {
    body.subnet = subnet.trim();
  }

  const retryFunction = () =>
    startOnvifScan({ tunnelId, credentials, subnet, navigate });

  AxiosUI.post("network-auto-discovery/", body).then(
    (response: AxiosResponse<DiscoveryCreateResponse>) => {
      const taskId = response.data?.task_id;
      if (!taskId) {
        setErrorMessage("Scan did not return a task id.");
        setScanStatus("failed");
        return;
      }
      setTaskId(taskId);
      pollOnvifScan(taskId, Date.now());
    },
    (reason: AxiosError) => {
      universalAPIErrorHandler(reason, navigate, retryFunction, () => {
        const data = reason.response?.data as { error?: string } | undefined;
        setErrorMessage(
          data?.error || "Failed to start the scan. Please try again."
        );
        setScanStatus("failed");
      });
    }
  );
};
